// =============================================
// File: src/components/activity/ExertionRecoveryActivityDetails.jsx
// Purpose: Thin presentational component using the hooks above
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ResultCard } from "../result/ResultCard";
import { ChartBlock } from "../result/ChartBlock";
import { SmallChartBlock } from "./SmallChartBlock";
import Spacer from "../ui/Spacer";

import { useExertionRecoverySummary } from "../../hooks/useExertionRecoverySummary";
import { useRRTrend } from "../../hooks/useRRTrend";
import { useRecoveryDatasets } from "../../hooks/useRecoveryDatasets";

import MetricTrendChart from "../charts/MetricTrendChart";
import MetricLineChart from "../charts/MetricLineChart";
import MetricBarChart from "../charts/MetricBarChart";
import MetricScatterChart from "../charts/MetricScatterChart";

import { ensureExertionAdvice } from "../../utils/ensureExertionAdvice";
import { AiContainer } from "./AiContainer";
import ActivityDetailsModal from "./ActivityDetailsModal";
import { useActivityData } from "../../hooks/useActivityData";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { useUserEmail } from "../../hooks/useUserEmail";

const ADVANCED_ALLOWED_EMAILS = new Set([
  "bendruce@icloud.com",
  "lmogg@electronrx.com",
  "kgozen@electronrx.com",
]);

export default function ExertionRecoveryActivityDetails({
  uid,
  baseName,
  date,
  data,
  locks = {},
  redemptionCTA,
  downloadURL,
}) {
  const { t } = useTranslation();

  const isDebug = process.env.REACT_APP_POD_ENV_ER !== "prod";

  // Local copy so UI updates immediately on save / skip
  const [localData, setLocalData] = useState(data || null);
  const { email: userEmail, loading: loadingEmail } = useUserEmail();

  // Keep localData in sync when parent data changes
  useEffect(() => {
    setLocalData(data || null);
  }, [data]);
  console.log(data);
  // Re-run the hook locally to get the update function (no extra blob fetch)
  const { loading: loadingActivityData, updateActivityData } = useActivityData({
    uid,
    baseName,
    tag: "breatheultra",
    fetchBlob: false,
  });

  // State for the activity details modal
  const hasDetailsLogged = localData?.activity_details_logged;
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // 1) Summary + derived metrics (these still come from the parent data,
  //    which is fine because they don't change when the user edits labels)
  const {
    subtitle,
    rrBpm,
    breathingRecovery60,
    breathingRecovery120,
    recoveryTime90,
    recoveryTime95,
  } = useExertionRecoverySummary({ date, data });

  // 2) RR trend sparkline (bucketed by day)
  const {
    loading: loadingRRPrev,
    labels: rrLabels,
    values: rrValues,
    minMax: rrMinMax,
    history: rrHistory,
  } = useRRTrend({
    uid,
    baseName,
    date,
    windowDays: 30,
    limit: undefined,
    repo: "pdx-recording-exertion-recovery",
    variant: "exertion",
  });

  // 3) Chart datasets for recovery curve + traces
  const {
    fitDataset,
    medianLineDataset,
    peaksLineDataset,
    peaksPointsDataset,
    xMax,
  } = useRecoveryDatasets({ data, t });

  const initialAdvice =
    localData?.aiCoachResponse?.exertion?.adviceByGroup ?? null;
  const [advice, setAdvice] = useState(initialAdvice);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Derived labels for activity details based on localData
  const sportLabel =
    localData?.sport &&
    t(`modal.sport.${localData.sport}`, {
      defaultValue: localData.sport,
    });

  const intensityLabel =
    localData?.intensity &&
    t(`modal.intensity.${String(localData.intensity).toLowerCase()}`, {
      defaultValue: localData.intensity,
    });

  // --- Modal Visibility Effect ---
  useEffect(() => {
    if (localData && !hasDetailsLogged && !loadingActivityData) {
      setShowDetailsModal(true);
    }
  }, [localData, hasDetailsLogged, loadingActivityData]);

  /**
   * Fetch AI advice.
   * @param {boolean} force - if true, ignore any existing advice and regenerate.
   * @param {object} overrides - optional fields to merge over localData for this call.
   */
  const handleGetAdvice = useCallback(
    async (force = false, overrides = {}) => {
      if (loading) return; // avoid overlapping calls
      setLoading(true);
      setErr("");

      try {
        const base = localData || {};
        const mergedData = { ...base, ...(overrides || {}) };

        // inside handleGetAdvice, replace the current `const stats = { ... }` with:

        const safeRrHistory = Array.isArray(rrHistory)
          ? rrHistory
              .filter((h) => h && h.label != null && h.rrBpm != null)
              .map((h) => ({
                label: String(h.label),
                rrBpm: Number(h.rrBpm),
              }))
          : undefined;

        const stats = {
          // Cloud Function expects timestamp as a string (optional)
          timestamp: date instanceof Date ? date.toISOString() : String(date),

          // core metrics (must be numbers)
          rrBpm: Number(mergedData?.rrBpm ?? rrBpm ?? 17),
          br1min: Number(
            mergedData?.breathingRecovery60 ?? breathingRecovery60 ?? 7.76
          ),
          br2min: Number(
            mergedData?.breathingRecovery120 ?? breathingRecovery120 ?? 8.97
          ),

          // recovery times as strings
          rt90: String(mergedData?.recoveryTime90 ?? recoveryTime90 ?? "1:14s"),
          rt95: String(mergedData?.recoveryTime95 ?? recoveryTime95 ?? "1:37s"),

          // optional profile-level context
          athleteLevel: mergedData?.athleteLevel ?? undefined,
          sportFocus: mergedData?.sportFocus ?? undefined,

          // history
          rrHistory: safeRrHistory,

          // user-provided activity details (all optional)
          activityTitle: mergedData?.title ?? undefined,
          activityExercise: mergedData?.exercise ?? undefined,
          activitySport: mergedData?.sport ?? undefined,
          activityDurationMin:
            mergedData?.duration_min != null
              ? Number(mergedData.duration_min)
              : undefined,
          activityIntensity: mergedData?.intensity ?? undefined,
          activityDescription: mergedData?.description ?? undefined,
        };

        const existingAdvice =
          !force && localData?.aiCoachResponse?.exertion?.adviceByGroup
            ? localData.aiCoachResponse.exertion.adviceByGroup
            : null;

        const adviceByGroup = await ensureExertionAdvice({
          uid,
          baseName,
          stats,
          existingAdvice,
        });

        setAdvice(adviceByGroup || null);
      } catch (e) {
        console.error(e);
        setErr(e.message || "Failed to fetch advice");
      } finally {
        setLoading(false);
      }
    },
    [
      uid,
      baseName,
      date,
      localData,
      rrBpm,
      breathingRecovery60,
      breathingRecovery120,
      recoveryTime90,
      recoveryTime95,
      rrHistory,
      loading,
    ]
  );

  // --- Modal Save Handler ---
  const handleSaveActivityDetails = useCallback(
    async (details) => {
      try {
        const patch = {
          ...details,
          activity_details_logged: true,
        };

        await updateActivityData(patch);

        // Optimistically update localData so UI reflects changes immediately
        setLocalData((prev) => ({
          ...(prev || {}),
          ...patch,
        }));

        setShowDetailsModal(false);

        // Generate advice using the latest details
        await handleGetAdvice(true, patch);
      } catch (error) {
        // let the modal surface its own error
        throw error;
      }
    },
    [updateActivityData, handleGetAdvice]
  );

  const handleSkip = useCallback(async () => {
    try {
      const patch = { activity_details_logged: true };

      await updateActivityData(patch);

      // Optimistically update localData
      setLocalData((prev) => ({
        ...(prev || {}),
        ...patch,
      }));

      setShowDetailsModal(false);

      // Generate advice even if they skipped extra details
      await handleGetAdvice(true, patch);
    } catch (error) {
      console.error(error);
    }
  }, [updateActivityData, handleGetAdvice]);

  const handleOpenEdit = useCallback(() => {
    setShowDetailsModal(true);
  }, []);

  const titleText =
    localData?.activity_details_logged && localData?.title
      ? localData.title
      : t("activity.exertion.title", {
          defaultValue: "Exertion Recovery Test",
        });

  // --- Cumulative sum of peak heights (peak_values) over time (peak_times_s) ---
  const cumulativePeaksDataset = useMemo(() => {
    const times = Array.isArray(data?.peak_times_s) ? data.peak_times_s : [];
    const values = Array.isArray(data?.peak_values) ? data.peak_values : [];

    if (!times.length || !values.length) return null;

    const n = Math.min(times.length, values.length);
    let running = 0;

    const points = [];
    for (let i = 0; i < n; i += 1) {
      const x = Number(times[i]);
      const v = Number(values[i]);

      if (!Number.isFinite(x) || !Number.isFinite(v)) continue;

      running += v;
      points.push({ x, y: running });
    }

    if (!points.length) return null;

    return {
      label: t("metrics.minuteVentilation.name", {
        defaultValue: "Minute Ventilation",
      }),
      data: points,
    };
  }, [data?.peak_times_s, data?.peak_values, t]);

  // --- 30s bins: sum of peak heights within each bin ---
  const minuteVentilationBins30s = useMemo(() => {
    const times = Array.isArray(data?.peak_times_s) ? data.peak_times_s : [];
    const values = Array.isArray(data?.peak_values) ? data.peak_values : [];
    if (!times.length || !values.length) return null;

    const n = Math.min(times.length, values.length);
    const binSize = 30; // seconds

    // Choose a stable end bound: use xMax if available, otherwise max time
    const maxT = Number.isFinite(xMax)
      ? Number(xMax)
      : Math.max(...times.map((t) => Number(t)).filter(Number.isFinite), 0);

    if (!Number.isFinite(maxT) || maxT <= 0) return null;

    const binCount = Math.ceil(maxT / binSize);
    const sums = new Array(binCount).fill(0);

    for (let i = 0; i < n; i += 1) {
      const t = Number(times[i]);
      const v = Number(values[i]);
      if (!Number.isFinite(t) || !Number.isFinite(v)) continue;
      if (t < 0) continue;

      const idx = Math.floor(t / binSize);
      if (idx >= 0 && idx < binCount) sums[idx] += v;
    }

    const labels = sums.map((_, i) => {
      const start = i * binSize;
      const end = (i + 1) * binSize;
      return `${start}-${end}s`;
    });

    return {
      labels,
      dataset: {
        label: t("metrics.minuteVentilationBins30s.name", {
          defaultValue: "Ventilation (30s bins)",
        }),
        data: sums,
      },
    };
  }, [data?.peak_times_s, data?.peak_values, xMax, t]);

  // --- Poincaré (timing) with time slider ---
  const peakTimes = useMemo(
    () => (Array.isArray(data?.peak_times_s) ? data.peak_times_s : []),
    [data?.peak_times_s]
  );

  const durationSeconds = useMemo(() => {
    // Use xMax if available; else last peak time; else 300.
    const lastT = peakTimes.length
      ? Number(peakTimes[peakTimes.length - 1])
      : 0;
    const d = Number.isFinite(xMax) ? Number(xMax) : lastT;
    return Number.isFinite(d) && d > 0 ? d : 300;
  }, [xMax, peakTimes]);

  const [poincareT, setPoincareT] = useState(() =>
    Math.min(60, durationSeconds)
  );

  // Keep slider bound valid if new data loads
  useEffect(() => {
    setPoincareT((prev) =>
      Math.min(Math.max(10, prev), Math.floor(durationSeconds))
    );
  }, [durationSeconds]);

  // --- Poincaré (timing) with point-by-point slider ---
  const primary = getPrimaryColour();

  const times = useMemo(
    () => peakTimes.map((t) => Number(t)).filter((t) => Number.isFinite(t)),
    [peakTimes]
  );

  // We build IBIs from times; N peaks -> (N-1) IBIs -> (N-2) Poincaré points
  const ibis = useMemo(() => {
    if (times.length < 2) return [];
    const out = [];
    for (let i = 0; i < times.length - 1; i += 1) {
      const dt = times[i + 1] - times[i];
      if (Number.isFinite(dt) && dt > 0) out.push(dt);
    }
    return out;
  }, [times]);

  const poincareAllPoints = useMemo(() => {
    if (ibis.length < 2) return [];
    const pts = [];
    for (let i = 0; i < ibis.length - 1; i += 1) {
      pts.push({ x: ibis[i], y: ibis[i + 1], i }); // keep index for highlighting if needed
    }
    return pts;
  }, [ibis]);

  const maxPoints = poincareAllPoints.length;

  // slider = how many points to show (1..maxPoints)
  const [poincareN, setPoincareN] = useState(maxPoints);

  // keep valid when data loads/changes
  useEffect(() => {
    setPoincareN((prev) => {
      const nextMax = Math.max(1, maxPoints);
      return Math.min(Math.max(1, prev), nextMax);
    });
  }, [maxPoints]);

  const poincareTimingDataset = useMemo(() => {
    const slice = poincareAllPoints.slice(0, poincareN);
    if (!slice.length) return null;

    return {
      label: "IBI Poincaré",
      data: slice.map(({ x, y }) => ({ x, y })),
      pointRadius: 3,
    };
  }, [poincareAllPoints, poincareN]);

  // Optional: show a “current point” marker (last point) so the slider feels animated
  const poincareCurrentPointDataset = useMemo(() => {
    if (!poincareAllPoints.length) return null;
    const idx = Math.min(Math.max(1, poincareN), poincareAllPoints.length) - 1;
    const p = poincareAllPoints[idx];
    if (!p) return null;

    return {
      label: "Current",
      data: [{ x: p.x, y: p.y }],
      pointRadius: 6,
      pointHoverRadius: 7,
      pointBackgroundColor: primary,
    };
  }, [poincareAllPoints, poincareN, primary]);

  // Fixed axis bounds based on ALL points (not the current slice)
  const poincareAxis = useMemo(() => {
    if (!poincareAllPoints.length) return null;

    let max = 0;
    for (const p of poincareAllPoints) {
      if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) continue;
      max = Math.max(max, p.x, p.y);
    }

    const pad = (max || 1) * 0.08;

    return {
      xMax: max + pad,
      yMax: max + pad,
    };
  }, [poincareAllPoints]);

  // Derive the elapsed time at the current slider position (nice UX)
  const poincareElapsedSeconds = useMemo(() => {
    // poincare point i uses IBIs i and i+1, which correspond to peaks up to index i+2
    // time at peak index (i+2) is times[i+2]
    if (!times.length) return 0;
    const i = Math.min(Math.max(1, poincareN), maxPoints) - 1;
    const peakIdx = i + 2;
    return Number.isFinite(times[peakIdx]) ? times[peakIdx] : 0;
  }, [times, poincareN, maxPoints]);

  const canSeeAdvanced =
    typeof userEmail === "string" &&
    ADVANCED_ALLOWED_EMAILS.has(userEmail.toLowerCase());

  return (
    <div className="w-full max-w-lg flex flex-col gap-4">
      {/* Activity Details Modal */}
      <ActivityDetailsModal
        isVisible={showDetailsModal}
        onSave={handleSaveActivityDetails}
        onSkip={handleSkip}
        initialData={localData || data}
      />
      {/* Main Result Card + Edit button overlay */}
      <div className="relative">
        <ResultCard title={titleText} subtitle={subtitle} />

        {localData?.activity_details_logged && (
          <button
            type="button"
            onClick={handleOpenEdit}
            className="absolute top-3 right-4 flex items-center gap-1 text-xs text-greyscale-3 hover:text-greyscale-2 bg-transparent"
          >
            <span className="material-symbols-outlined text-[18px]" aria-hidden>
              edit
            </span>
          </button>
        )}
      </div>
      {/* Debug: Regenerate advice */}
      {isDebug && (
        <div className="flex justify-end -mt-2">
          <button
            type="button"
            onClick={() => handleGetAdvice(true)}
            className="text-xs underline text-primary/80 hover:text-primary disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Regenerating advice…" : "Regenerate AI advice"}
          </button>
        </div>
      )}
      {/* Full activity details block */}
      {localData?.activity_details_logged && (
        <>
          <div className="flex flex-wrap gap-2 w-full items-start justify-between">
            <div className="flex flex-row w-fit gap-2 items-center justify-start">
              <span
                className="material-symbols-outlined text-[24px] no-underline"
                aria-hidden="true"
              >
                stadium
              </span>
              {sportLabel || localData.sport || "N/A"}
            </div>

            <div className="flex flex-row w-fit gap-2 items-center justify-start">
              <span
                className="material-symbols-outlined text-[24px] no-underline"
                aria-hidden="true"
              >
                trophy
              </span>
              {localData.exercise || "N/A"}
            </div>

            <div className="flex flex-row w-fit gap-2 items-center justify-start">
              <span
                className="material-symbols-outlined text-[24px] no-underline"
                aria-hidden="true"
              >
                avg_time
              </span>
              {localData.duration_min != null
                ? `${localData.duration_min} min`
                : "N/A"}
            </div>

            <div className="flex flex-row w-fit gap-2 items-center justify-start">
              <span
                className="material-symbols-outlined text-[24px] no-underline"
                aria-hidden="true"
              >
                azm
              </span>
              {intensityLabel || localData.intensity || "N/A"}
            </div>
            {localData.description && (
              <div className="flex flex-row w-full gap-2 items-start justify-start">
                <span
                  className="material-symbols-outlined text-[24px] no-underline mt-[2px]"
                  aria-hidden="true"
                >
                  notes
                </span>
                <p className="text-sm text-greyscale-2">
                  {localData.description}
                </p>
              </div>
            )}
          </div>

          <Spacer />
        </>
      )}
      <SmallChartBlock
        title={t("metrics.recoveryTime90.name", { defaultValue: "" })}
        infoKey="info.recoveryTime"
        value={recoveryTime90}
        unit={t("metrics.recoveryTime90.unit", { defaultValue: "s" })}
      />
      <Spacer />
      <SmallChartBlock
        title={t("metrics.recoveryTime95.name", { defaultValue: "" })}
        infoKey="info.recoveryTime"
        value={recoveryTime95}
        unit={t("metrics.recoveryTime95.unit", { defaultValue: "s" })}
      />
      <Spacer />
      <AiContainer
        loading={loading}
        advice={advice}
        err={err}
        children={
          !loading &&
          advice &&
          !err && (
            <div className="flex flex-col gap-1 text-sm">
              {advice.recoveryTime?.feedback && (
                <p>{advice.recoveryTime.feedback}</p>
              )}
              {advice.recoveryTime?.suggestion && (
                <p className="text-muted-foreground">
                  {advice.recoveryTime.suggestion}
                </p>
              )}
            </div>
          )
        }
      />
      {/* Top metrics */}
      <SmallChartBlock
        title={t("metrics.breathingRecovery60.name", { defaultValue: "" })}
        infoKey="info.breathingRecovery"
        value={breathingRecovery60}
        unit={t("metrics.breathingRecovery60.unit", { defaultValue: "s" })}
      />
      <Spacer />
      <SmallChartBlock
        title={t("metrics.breathingRecovery120.name", { defaultValue: "" })}
        infoKey="info.breathingRecovery"
        value={breathingRecovery120}
        unit={t("metrics.breathingRecovery120.unit", { defaultValue: "s" })}
      />
      <AiContainer
        loading={loading}
        advice={advice}
        err={err}
        children={
          !loading &&
          advice &&
          !err && (
            <div className="flex flex-col gap-1 text-sm">
              {advice.breathingRecovery?.feedback && (
                <p>{advice.breathingRecovery.feedback}</p>
              )}
              {advice.breathingRecovery?.suggestion && (
                <p className="text-muted-foreground">
                  {advice.breathingRecovery.suggestion}
                </p>
              )}
            </div>
          )
        }
      />
      {/* RR Trend */}
      <SmallChartBlock
        title={t("metrics.respiratoryRate.name", {
          defaultValue: "Respiratory Rate",
        })}
        infoKey="info.respiratoryRate"
        value={rrBpm}
        unit={t("metrics.respiratoryRate.unit", { defaultValue: "bpm" })}
      >
        <MetricTrendChart
          height={96}
          labels={rrLabels}
          values={rrValues}
          minMax={rrMinMax}
          lineLabel={t("metrics.rr", { defaultValue: "Respiratory Rate" })}
          loading={loadingRRPrev}
          yTickStep={1}
          yDecimals={0}
          showMinMaxBars={true}
          minMaxWidth={8}
        />
      </SmallChartBlock>
      <AiContainer
        loading={loading}
        advice={advice}
        err={err}
        children={
          !loading &&
          advice &&
          !err && (
            <div className="flex flex-col gap-1 text-sm">
              {advice.rr?.feedback && <p>{advice.rr.feedback}</p>}
              {advice.rr?.suggestion && (
                <p className="text-muted-foreground">{advice.rr.suggestion}</p>
              )}
            </div>
          )
        }
      />
      {/* Recovery Curve */}
      <ChartBlock
        title={t("metric.recoveryCurve.name", {
          defaultValue: "Recovery Curve",
        })}
        infoKey="info.recoveryCurve"
      >
        <MetricLineChart
          height={220}
          datasets={fitDataset ? [fitDataset] : []}
          preset="none"
          showLegend={false}
          showTooltip={false}
          xType="linear"
          xMin={0}
          xMax={xMax}
          xTickStep={60}
          xTickFormat={(v) => `${v}s`}
          yDecimals={1}
          xLabel={t("activity.spirometry.timeLabel", {
            defaultValue: "Time (s)",
          })}
        />
      </ChartBlock>
      <AiContainer
        loading={loading}
        advice={advice}
        err={err}
        children={
          !loading &&
          advice &&
          !err && (
            <div className="flex flex-col gap-1 text-sm">
              {advice.recoveryCurve?.feedback && (
                <p>{advice.recoveryCurve.feedback}</p>
              )}
              {advice.recoveryCurve?.suggestion && (
                <p className="text-muted-foreground">
                  {advice.recoveryCurve.suggestion}
                </p>
              )}
            </div>
          )
        }
      />

      {canSeeAdvanced && (
        <>
          {/* Cumulative Peak Heights */}
          <ChartBlock
            title={t("metrics.minuteVentilation.name", {
              defaultValue: "Minute Ventilation",
            })}
            infoKey="info.minuteVentilation"
          >
            <MetricLineChart
              height={220}
              datasets={cumulativePeaksDataset ? [cumulativePeaksDataset] : []}
              preset="none"
              showLegend={false}
              showTooltip={false}
              xType="linear"
              xMin={0}
              xMax={xMax}
              xTickStep={60}
              xTickFormat={(v) => `${v}s`}
              yDecimals={1}
              xLabel={t("activity.spirometry.timeLabel", {
                defaultValue: "Time (s)",
              })}
            />
          </ChartBlock>
          {/* Minute Ventilation (30s bins) */}
          <ChartBlock
            title={t("metrics.minuteVentilationBins30s.name", {
              defaultValue: "Ventilation (30s bins)",
            })}
            infoKey="info.minuteVentilationBins30s"
          >
            <MetricBarChart
              height={180}
              labels={minuteVentilationBins30s?.labels ?? []}
              datasets={
                minuteVentilationBins30s
                  ? [minuteVentilationBins30s.dataset]
                  : []
              }
              showLegend={false}
              showTooltip={false}
              xLabel={t("activity.spirometry.timeLabel", {
                defaultValue: "Time (s)",
              })}
              yDecimals={1}
            />
          </ChartBlock>

          <ChartBlock
            title={t("metrics.poincareTiming.title", {
              defaultValue: "Breathing Variability (Poincaré)",
            })}
            infoKey="info.poincareTiming"
          >
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-typography/70">
                  {t("metrics.poincareTiming.window", {
                    defaultValue: "Progress",
                  })}
                  : {poincareN}/{maxPoints}{" "}
                  {maxPoints > 0 && (
                    <span className="text-typography/50">
                      ({Math.round(poincareElapsedSeconds)}s)
                    </span>
                  )}
                </div>

                <div className="text-xs text-typography/70">
                  {t("metrics.poincareTiming.points", {
                    defaultValue: "Points",
                  })}
                  : {poincareTimingDataset?.data?.length ?? 0}
                </div>
              </div>

              <input
                type="range"
                min={1}
                max={Math.max(1, maxPoints)}
                step={1} // <-- point-by-point
                value={poincareN}
                onChange={(e) => setPoincareN(Number(e.target.value))}
                className="w-full accent-primary" // <-- Tailwind: uses your theme primary
                aria-label="Poincaré point slider"
              />
              <MetricScatterChart
                height={260}
                datasets={
                  poincareTimingDataset
                    ? [
                        poincareTimingDataset,
                        ...(poincareCurrentPointDataset
                          ? [poincareCurrentPointDataset]
                          : []),
                      ]
                    : []
                }
                xLabel={t("metrics.poincareTiming.xLabel", {
                  defaultValue: "IBIₙ (s)",
                })}
                yLabel={t("metrics.poincareTiming.yLabel", {
                  defaultValue: "IBIₙ₊₁ (s)",
                })}
                showLegend={false}
                showTooltip={true}
                xMax={poincareAxis?.xMax}
                yMax={poincareAxis?.yMax}
              />
            </div>
          </ChartBlock>

          <Spacer />
        </>
      )}

      {/* Breathing Trace */}
      <ChartBlock
        title={t("activity.exertion.breathingTraceTitle", {
          defaultValue: "Breathing Trace",
        })}
        infoKey="info.breathingTrace"
      >
        {/* Line only */}
        <MetricLineChart
          height={220}
          datasets={peaksLineDataset ? [peaksLineDataset] : []}
          preset="none"
          showLegend={false}
          showTooltip={false}
          xType="linear"
          xMin={0}
          xMax={xMax}
          xTickStep={60}
          xTickFormat={(v) => `${v}s`}
          yDecimals={1}
          xLabel={t("activity.spirometry.timeLabel", {
            defaultValue: "Time (s)",
          })}
        />

        {/* Points only */}
        <MetricLineChart
          height={220}
          datasets={peaksPointsDataset ? [peaksPointsDataset] : []}
          preset="none"
          showLegend={false}
          showTooltip={false}
          xType="linear"
          xMin={0}
          xMax={xMax}
          xTickStep={60}
          xTickFormat={(v) => `${v}s`}
          yDecimals={1}
          xLabel={t("activity.spirometry.timeLabel", {
            defaultValue: "Time (s)",
          })}
        />

        {/* Median line */}
        <MetricLineChart
          height={220}
          datasets={medianLineDataset ? [medianLineDataset] : []}
          preset="none"
          showLegend={false}
          showTooltip={false}
          xType="linear"
          xMin={0}
          xMax={xMax}
          xTickStep={60}
          xTickFormat={(v) => `${v}s`}
          yDecimals={1}
          xLabel={t("activity.spirometry.timeLabel", {
            defaultValue: "Time (s)",
          })}
        />
      </ChartBlock>
    </div>
  );
}
